// src/sentry.js
//
// Centralised Sentry configuration. Errors that break a React render or
// throw uncaught at any other point in the browser get reported to your
// Sentry project so they actually surface to you instead of dying
// silently in users' browsers.
//
// Required env var (set in Vercel project settings → Environment):
//   VITE_SENTRY_DSN   — https://...@oXXXXX.ingest.us.sentry.io/...
//
// Optional env vars:
//   VITE_SENTRY_ENV   — overrides the environment tag in Sentry. Defaults
//                       to Vite's MODE (e.g. "production", "development").
//                       Vercel preview deploys read MODE = "production",
//                       so set this explicitly to "preview" on preview
//                       environments if you want them tagged separately.
//
// initSentry() is a safe no-op when the DSN is absent — local dev stays
// quiet without anyone having to wire dotenv. PROD deploys without the
// DSN log a single console warning so the misconfiguration is loud
// enough to notice.

import * as Sentry from "@sentry/react";

let initialised = false;

export function initSentry() {
  if (initialised) return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.PROD) {
      // Loud but not crashing: a prod build that forgot the env var
      // shouldn't take the site down, but you should know it happened.
      console.warn(
        "[sentry] VITE_SENTRY_DSN not set — errors won't be reported."
      );
    }
    return;
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENV || import.meta.env.MODE,
    // Sample 10% of routine transactions for performance traces. Errors
    // are always captured regardless of this rate.
    tracesSampleRate: 0.1,
  });

  initialised = true;

  // If supabaseClient already ran its auth bootstrap before this module
  // initialised (likely — it's imported eagerly by Landing/AuthProvider),
  // pick up the user it cached on window so the very first event we
  // send still has user context attached.
  if (typeof window !== "undefined" && window.__supabase_user) {
    setSentryUser(window.__supabase_user);
  }
}

export function setSentryUser(user) {
  if (!initialised) return;
  Sentry.setUser(
    user ? { id: user.id, email: user.email } : null
  );
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
